import { ReactNode } from 'react'

import { Library, PrimaryKey } from '@/generated/graphql'

export interface ICover {
  formatType: number
  largeImageUrl: string | null
  mediumImageUrl: string | null
  smallImageUrl: string | null
  type: number | null
  source: number | null
}

export interface IAuthor {
  Id: string
  ShardId: string
  FirstName: string
  LastName: string
  AuthorRole?: string
  AuthorRoleOrder?: number
  Isbn13?: string | null
}

export interface ICollection {
  Domain: string
  Subdomain: string
  Token: string
}

export interface IThumbnailUrl {
  thumbnailUrl: string
  source: number
}

export enum TLoginType {
  DIRECT = 'DIRECT',
  THIRD_PARTY = 'THIRD_PARTY',
}

export interface IAuthParams {
  Username: string
  Password: string
}

export interface IConfirmSignUpParams {
  Username: string
  ConfirmationCode: string
}

export interface IForgotPasswordParams {
  Username: string
}

export interface IConfirmForgotPasswordParams {
  Username: string
  Password: string
  ConfirmationCode: string
}

export interface IEmailParams {
  Username: string
}

export interface ICodeDeliveryDetails {
  Destination: string
  DeliveryMedium: string
  AttributeName: string
}

export interface IResendConfirmationCodeData {
  CodeDeliveryDetails: ICodeDeliveryDetails
}

export interface ISignUpData {
  UserConfirmed: boolean
  CodeDeliveryDetails: ICodeDeliveryDetails
  UserSub: string
}

export interface ISignInData {
  AuthenticationResult?: {
    AccessToken: string
    ExpiresIn: string
    TokenType: string
    RefreshToken: string
    IdToken: string
  }
  Session?: string
  ChallengeName?: string
}

export interface IChallengePasswordParams {
  Username: string
  NewPassword: string
  Session: string
}

export interface IError {
  message: string
  statusCode: string
  code: string
}

export interface IRefreshTokenParams {
  RefreshToken: string
}

export interface IRefreshTokenData {
  IdToken: string
  AccessToken: string
}

export interface IAvatarUrl {
  smallImageUrl: string
  mediumImageUrl: string
  largeImageUrl: string
  type: number | null
  source: number | null
}

export interface IRole {
  ShardId: string
  Id: string
  LibraryKeys: string
  IsAllLibraries: boolean
}

export interface IGlobalSignOutParams {
  AccessToken: string
}

export interface IRevokeTokenParams {
  Token: string
}

export interface IChangePasswordParams {
  PreviousPassword: string
  ProposedPassword: string
}

export enum TViewMode {
  LIST = 'LIST',
  GRID = 'GRID',
}

export interface MenuItemProps {
  label: string
  icon?: ReactNode
  onClick?: (row?: Library) => void
  className?: string
}

export enum TChangeQuantity {
  ADD = 'ADD',
  SUBTRACT = 'SUBTRACT',
}

export enum TItemActions {
  RENAME = 'RENAME',
  DELETE = 'DELETE',
  DUPLICATE = 'DUPLICATE',
  CHANGE = 'CHANGE',
  EDIT = 'EDIT',
  SHARE = 'SHARE',
  BORROW = 'BORROW',
  RESTORE = 'RESTORE',
  RETURN = 'RETURN',
  RENEW = 'RENEW',
  BARCODE = 'BARCODE',
}

export interface ItemInput {
  copyIndex: number
  internalBarcode?: string | null
  condition?: string | null
}

export interface Author {
  FirstName?: string
  LastName?: string
}

export interface IQuantity {
  isWish: boolean
  quantity: number
  shelfKey: string
  shelfPath: string
}

export interface IBookForm {
  libraryId: string
  name: string
  subName?: string
  originalName?: string
  sortedName?: string
  publisherName?: string
  publicationDateStr?: string
  isbn13?: string
  isbn10?: string
  language?: string
  originalLanguage?: string
  series?: string
  volume?: string
  edition?: string
  genres?: string
  description?: string
  coversJson?: string
  tags?: string
  authors?: Author[] | IAuthor[]
  formatType?: string
  numberOfPages?: number
  dewey?: string
  price?: number
  currency?: string
}

export interface ITag {
  Name: string
  Id?: string
  ShardId?: string
}

export interface IBookFormData {
  libraryId?: string
  name: string
  subName?: string
  originalName?: string
  sortedName?: string
  publisherName?: string
  publicationDate?: string
  publicationDateStr?: string
  isbn13?: string
  isbn10?: string
  ean?: string
  language?: string
  originalLanguage?: string
  series?: string
  volume?: string
  genres?: string
  description?: string
  coversJson?: string
  tags?: ITag[]
  authors?: IAuthor[] | Author[]
  numberOfPages?: number
  id?: string
  priceOnCover?: number
  avgRating?: number
  priceUnit?: number
  purchasePrice?: number
  formatType?: number
  dewey?: string
  currency?: string
  edition?: string
  supplierDetailsJson?: string
}

export enum TCollectionType {
  WISHLIST = 0,
  TAG = 1,
  SHELF = 2,
}

export enum TOrganizationTabs {
  INFORMATION = 'information',
  MANAGEMENT = 'management',
}

export enum TMemberTabs {
  ROLE = 'role',
  PROFILE = 'profile',
}

export enum TAccountTabs {
  PROFILE = 'profile',
  CHANGE_PASSWORD = 'change_password',
  QR_CODE = 'qr_code',
}

export enum TSettingTabs {
  IMPORT = 'import',
  SYSTEM = 'system',
}

export enum TRole {
  TENANT_ADMIN = 'tenant_admin',
  LIBRARIAN = 'librarian',
  MEMBER = 'member',
  GUEST = 'guest',
}

export enum TCoverType {
  FRONT_COVER = 0,
  BACK_COVER = 1,
  SIDE_COVER = 2,
}

export enum TCoverSource {
  S3 = 0,
  THIRD_PARTY = 1,
  LOCAL = 2,
}

export interface IImage {
  largeImageUrl: string | null
  mediumImageUrl: string | null
  smallImageUrl: string | null
  type: number | null
  source: number | null
}

export interface ISearchForm {
  libraryId: string
  search: string
}

export interface ISearchOnline {
  numberOfItems: number
  items: IBookFormData[]
  isUpdate?: boolean
}

export interface IBookSearchGoogle {
  id: string
  volumeInfo: {
    title: string
    authors: string[]
    publisher: string
    publishedDate: string
    pageCount: number
    categories?: string[]
    averageRating?: number
    description: string
    imageLinks?: {
      thumbnail: string
      smallThumbnail: string
    }
    language: string
    infoLink?: string
    industryIdentifiers?: IndustryIdentifier[]
  }
  saleInfo?: {
    isEbook?: boolean
    retailPrice?: {
      amount: number
      currencyCode: string
    }
  }
}

export interface IndustryIdentifier {
  type: string
  identifier: string
}

export interface IBookSearchAmazons {
  SearchResult: {
    Items: IBookSearchAmazon[]
  }
}

export interface IBookSearchAmazon {
  ASIN: string
  DetailPageURL: string
  Images?: {
    Primary?: {
      Small?: {
        URL?: string
      }
      Medium?: {
        URL?: string
      }
      Large?: {
        URL?: string
      }
    }
  }
  ItemInfo?: {
    ByLineInfo?: {
      Brand?: {
        DisplayValue?: string
        Label?: string
        Locale?: string
      }
      Contributors?: Contributors[]
      Manufacturer?: {
        DisplayValue?: string
        Label?: string
        Locale?: string
      }
    }
    ContentInfo?: {
      Languages?: {
        DisplayValues?: Language[]
        Label?: string
        Locale?: string
      }
      PublicationDate?: {
        DisplayValue?: string
        Locale?: string
      }
      PagesCount?: {
        DisplayValue?: number
      }
    }
    ExternalIds?: {
      ISBNs: {
        DisplayValues: string[]
      }
      EANs: {
        DisplayValues: string[]
      }
    }
    Features: {
      DisplayValues: string[]
    }
    Title: {
      DisplayValue: string
    }
    Classifications?: {
      Binding?: {
        DisplayValue: string
      }
    }
  }
  Offers?: {
    Listings?: {
      Price: Price
    }[]
  }
  BrowseNodeInfo?: {
    BrowseNodes?: {
      DisplayName: string
    }[]
  }
}

export interface Price {
  Amount: string
  Currency: string
  DisplayAmount: string
}
export interface Contributors {
  Locale?: string
  Name?: string
  Role?: string
}

export interface Language {
  DisplayValue?: string
  Type?: string
}

export interface IGoodreadsSearchResponse {
  GoodreadsResponse: {
    search: {
      results: {
        work: IBookSearchGoodreads[] | IBookSearchGoodreads
      }
    }
  }
}

export interface IBookSearchGoodreads {
  id: string
  best_book: {
    id: string
    title: string
    author: {
      id: string
      name: string
    }
    small_image_url: string
    image_url: string
  }
  original_publication_day: string
  original_publication_month: string
  original_publication_year: string
}

export interface IGoodreadsBookchResponse {
  GoodreadsResponse: {
    book: IBookByIdSearchGoodreads
  }
}

interface IAuthorSearchGoodreads {
  id: string
  name: string
}
export interface IBookByIdSearchGoodreads {
  id: string
  title: string
  authors: {
    author: IAuthorSearchGoodreads[] | IAuthorSearchGoodreads
  }
  small_image_url: string
  image_url: string
  description: string
  isbn: string
  isbn13: string
  language_code: string
  num_pages: string
  averageRating?: number
  link?: string
}

export interface IDataSearchGoogle {
  totalItems: number
  items: IBookSearchGoogle[]
}

export interface IDataSkoolibVi {
  found: number
  hits: IBookSkoolibVi[]
}

export interface IBookSkoolibVi {
  document: {
    product_id: string
    authors: string[]
    categories: string[]
    category: string
    description: string
    link: string
    price: number
    publication_date: number
    publication_year: number
    sellers: string[]
    short_description: string
    thumbnail_url: string
    title: string
    ean: string
    isbn13: string
    manufacturer: string
    number_of_page: number
    publisher: string
    book_cover: string
  }
  highlights: {
    field: string
    matched_tokens: string[]
    snippet: string
  }[]
}

export interface IDataRakuten {
  count: number
  first: number
  hits: number
  last: number
  page: number
  pageCount: number
  Items: {
    Item: IDataRakutenItem
  }[]
}

export interface IDataRakutenItem {
  affiliateUrl: string
  author: string
  authorKana: string
  availability: string
  booksGenreId: string
  chirayomiUrl: string
  contents: string
  isbn: string
  itemCaption: string
  itemPrice: number
  largeImageUrl: string
  mediumImageUrl: string
  smallImageUrl: string
  postageFlag: number
  publisherName: string
  salesDate: string
  seriesName: string
  size: string
  subTitle: string
  title: string
  itemUrl: string
  reviewCount: number
  reviewAverage: number
}

export interface IDataNaver {
  total: number
  items: IDataNaverItem[]
}

export interface IDataNaverItem {
  title: string
  link: string
  image: string
  author: string
  discount: string
  publisher: string
  pubdate: string
  isbn: string
  description: string
}

export interface ISupplierDetails {
  item: { supplier: string; productId: string }
  itemUrl: string | null
  affiliateUrl: string | null
  displayAmount: string | null
  priceOnCover: number | null
  currency: string | null
}

export interface IRoleForm {
  name: string
  description?: string
  permissionIds: string[]
  hierachy: number
}

export interface IRoleSubmit {
  name: string
  description?: string
  permissionIds: string
  hierachy: number
}

export enum THeaderPermission {
  ENTITY = 'entity',
  VIEW = 'view',
  UPDATE = 'update',
  ADD_EDIT = 'add_edit',
  CREATE = 'create',
  DELETE = 'delete',
  SOFT_DELETE = 'soft_delete',
  ALL_METHOD = 'all_method',
  RESTORE = 'restore',
}

export enum TRolePermission {
  ORGANIZATION = 'organization',
  LIBRARY = 'library',
  ROLE = 'role',
  MEMBER = 'member',
  LOAN = 'loan_transaction',
  SHELF = 'shelf',
  TAG = 'tag',
  TITLE = 'title',
  AUTHOR = 'author',
  ITEM = 'item',
}

export type PermissionMap = Record<TRolePermission, Partial<Record<THeaderPermission, string[]>>>
export interface IMemberForm {
  username?: string
  email?: string
  isOffline: boolean
  role?: IRoleMemberForm
}

export interface IRoleMemberForm {
  shardId: string
  id: string
  libraryKeys?: PrimaryKey[]
  isAllLibraries: boolean
}

export enum TMemberStatus {
  UNDEFINED = -1,
  INVITED = 0,
  ACTIVE = 1,
  INACTIVE = 2,
  OFFLINE = 3,
  DELETED = 4,
}

export enum TItemStatus {
  AVAILABLE = 1,
  BORROWED = 2,
  LOST = 3,
}

export interface StatusType {
  color: string
  displayName: string
}

export enum TSearchKey {
  GOOGLE_CUSTOM_SEARCH_API_KEY_SECRET_NAME = 'GoogleCustomSearchAPIKey',
}

export interface SecretValue {
  GoogleCustomSearchAPIKey?: string
}

export interface IMember {
  Id: string
  ShardId: string
  Name: string
}

export enum TSubscription {
  FREE = 'free',
  BASIC = 'basic',
  STANDARD = 'standard',
  PREMIUM = 'premium',
}

export interface ISubscriptionPlan {
  price: string
  label: TSubscription
  features: {
    label: string
    num: string
    access: boolean
  }[]
}

export interface IUserPayload {
  sub: string
  email_verified: boolean
  iss: string
  'custom:join_tenant_ids': string
  'cognito:username': string
  given_name: string
  picture: string
  origin_jti: string
  aud: string
  identities: Identity[]
  event_id: string
  token_use: string
  auth_time: number
  'custom:active_tenant_id': string
  exp: number
  iat: number
  family_name: string
  jti: string
}

export interface Identity {
  dateCreated: string
  userId: string
  providerName: string
  providerType: string
  issuer: string | null
  primary: string
}

export enum TSizeImage {
  SMALL = 'smallImageUrl',
  MEDIUM = 'mediumImageUrl',
  LARGE = 'largeImageUrl',
}

export interface IValueNotification {
  AccountId: string
  UserId: string
  LibraryName: string
  OrganizationName: string
  Titles: {
    TitleName: string
    NumberOfItems: number
  }[]
  DateReturned: string
  DateStartLoan: string
}

export enum TEventType {
  CHECKOUT_LOAN = 'CHECKOUT_LOAN_TRANSACTION',
  CHECKIN_LOAN = 'CHECKIN_LOAN_TRANSACTION',
  RENEW_LOAN = 'RENEW_LOAN_TRANSACTION',
}

export interface ICurrency {
  code: string
  symbol: string
  name: string
  symbolNative: string
  decimalDigits: number
  locale: {
    country: string
    language: string
  } | null
}

export enum TSupplier {
  GOODREADS = 'GOODREADS',
  GOOGLE = 'GOOGLE',
  AMAZON = 'AMAZON',
  SKOOLIB_VI = 'SKOOLIB_VI',
  RAKUTEN = 'RAKUTEN',
  NAVER = 'NAVER',
}

export enum FormatType {
  EBOOK = 3,
  NONE = -1,
}
